import React from 'react';
import { useIntl } from 'react-intl';
import Button from 'components/button';
import BackgroundImage from 'components/background-image';
import Html from 'components/html';
import Icon, { IconList } from 'components/icon';
import type { ResponsiveImageOutput } from 'contracts';

import Models from './assets/models.jpg';
import ModelsMobile from './assets/models-mobile.jpg';
import EquationBox from './equation-box';
import ExpandedEquation from './expanded-equation';
import tracking from './what-you-get-banner.tracking';
import { config, testId } from './what-you-get-banner.settings';
import messages from './what-you-get-banner.i18n';
import styles from './what-you-get-banner.module.scss';

const WhatYouGetBanner: React.FunctionComponent<unknown> = () => {
  const [expanded, setExpanded] = React.useState(false);
  const { formatMessage, formatNumber } = useIntl();
  const translation = messages.pages.landing.whatYouGetBanner.livejasmin;

  const handleExpand = React.useCallback(() => {
    setExpanded(true);
  }, []);

  return (
    <Html.div
      className={[styles.whatYouGetBanner, expanded && styles.expanded, 'p-0 m-0w-100']}
      testId={testId.component}
    >
      <BackgroundImage
        className={[styles.bg, 'w-100 h-100']}
        src={Models as ResponsiveImageOutput}
        mobileSrc={ModelsMobile as ResponsiveImageOutput}
      />
      <Html.div className={styles.shade} />
      <Html.div className={[styles.spotlight, styles.light1, 'd-none d-md-block']} />
      <Html.div className={[styles.spotlight, styles.light2, 'd-none d-md-block']} />
      <Html.div className={[styles.header, 'w-100 px-6']}>
        <Html.div className={[styles.whatYouGet, 'mb-3']}>
          {formatMessage(translation.whatYouGet, {
            b: (contents: React.ReactNode) => <Html.b className={styles.highlight}>{contents}</Html.b>,
          })}
        </Html.div>
        <Html.div className={styles.description}>{formatMessage(translation.maximize)}</Html.div>
        <Html.div className={[styles.example, 'mt-n7']}>{formatMessage(translation.example)}</Html.div>
      </Html.div>
      <Html.div className={[styles.introEquation, 'w-100 w-md-auto px-4']}>
        <EquationBox
          className={[styles.box, styles.earnUpToBox]}
          header={formatMessage(translation.earnUpTo)}
          amount={formatNumber(config.pctRevenueShare, { style: 'percent' })}
          footer={formatMessage(translation.revenueShare)}
          variant="intro"
        />
        <Icon name={IconList.plusSolid} className={styles.plusSolid} />
        <EquationBox
          className={[styles.box, styles.extraBox]}
          header={formatMessage(translation.extra)}
          amount={formatNumber(config.pctReferBonus, { style: 'percent' })}
          footer={formatMessage(translation.referBonus)}
          variant="intro"
        />
      </Html.div>
      <Html.div className={styles.expandedEquation}>
        <Html.div className={styles.scroller}>
          <ExpandedEquation />
        </Html.div>
      </Html.div>
      <Html.div className={[styles.footer, 'w-100 px-6']}>
        <Button
          className={styles.expandButton}
          variant="text-primary"
          onClick={handleExpand}
          testId={testId.expandButton}
          tracking={tracking.seeExample}
        >
          {formatMessage(translation.seeExample)}
        </Button>
        <Html.div className={[styles.numbersVary, 'mt-5 mt-md-12']}>{formatMessage(translation.numbersVary)}</Html.div>
      </Html.div>
    </Html.div>
  );
};

export default WhatYouGetBanner;
